import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, StatusBadge } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function Documents() {
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [genFor, setGenFor] = useState(null);
  const [empId, setEmpId] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/employer/document-templates").then(({ data }) => setTemplates(data.templates)).catch(() => {});
    api.get("/employer/documents").then(({ data }) => setDocuments(data.documents)).catch(() => {});
    api.get("/employer/employees").then(({ data }) => setEmployees(data.employees)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    if (!empId) {
      toast.error("Select an employee");
      return;
    }
    setBusy(true);
    try {
      await api.post("/employer/documents/generate", { template_id: genFor.id, employee_id: empId });
      toast.success(`${genFor.name} generated`);
      setGenFor(null);
      setEmpId("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const send = async (doc) => {
    try {
      await api.post(`/employer/documents/${doc.id}/send`);
      toast.success("Document sent to employee");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="documents-page">
      <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Document templates</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {templates.map((t) => (
          <div key={t.id} className="bg-white border border-border/60 rounded-xl p-5 card-hover" data-testid={`template-${t.id}`}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#2563EB]/10"><FileText className="w-4 h-4 text-[#2563EB]" /></div>
              <p className="font-heading font-semibold text-sm text-[#0B1B3D]">{t.name}</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-full mt-4" onClick={() => setGenFor(t)} data-testid={`generate-${t.id}`}>
              Generate
            </Button>
          </div>
        ))}
      </div>

      <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Generated documents</h3>
      {documents.length === 0 ? (
        <EmptyState title="No documents yet" description="Generate an offer, appointment letter or NDA from a template above." testId="documents-empty" />
      ) : (
        <div className="space-y-3" data-testid="documents-list">
          {documents.map((d) => (
            <div key={d.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`document-${d.id}`}>
              <div>
                <p className="font-semibold text-sm text-[#0B1B3D]">{d.template_name} — {d.variables?.employee_name}</p>
                <p className="text-xs text-muted-foreground mt-1">{fmtDate(d.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={d.status} />
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewDoc(d)} data-testid={`view-doc-${d.id}`}>View</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={async () => {
                  try {
                    const res = await api.get(`/documents/${d.id}/pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${d.template_name.replace(/\s+/g, "_")}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) { toast.error(errMsg(e)); }
                }} data-testid={`pdf-doc-${d.id}`}>PDF</Button>
                {d.status === "generated" && (
                  <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => send(d)} data-testid={`send-doc-${d.id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Send
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!genFor} onOpenChange={() => setGenFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="generate-dialog">
          <DialogHeader><DialogTitle>Generate {genFor?.name}</DialogTitle></DialogHeader>
          <div>
            <Label>Employee</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger className="mt-1.5" data-testid="generate-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">Variables like name, designation, salary and joining date are filled automatically.</p>
          </div>
          <Button onClick={generate} disabled={busy || !empId} className="w-full rounded-full bg-[#0B1B3D]" data-testid="generate-confirm-btn">
            {busy ? "Generating…" : "Generate Document"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="view-doc-dialog">
          <DialogHeader><DialogTitle>{viewDoc?.template_name}</DialogTitle></DialogHeader>
          <div className="bg-white border border-border rounded-lg p-6 whitespace-pre-line text-sm leading-relaxed font-serif max-h-[60vh] overflow-y-auto">
            {viewDoc?.body}
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="print-doc-btn">Print / Save PDF</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
